<template>
  <div
    v-if="wapers.length"
    class="waper-slider-container"
  >
    <ClientOnly>
      <Carousel
        ref="carousel"
        v-bind="carouselProps"
        :initial-slide="initialSlide"
        @slide-start="(data) => changeSlide(data as SlideStartEvent)"
        @slide-end="afterSlide"
      >
        <Slide
          v-for="wapIndex in Math.ceil(wapers.length / nbLines)"
          :key="wapIndex"
          class="slide"
        >
          <div
            v-for="wapInnerIndex in maxLines(wapIndex)"
            :key="wapInnerIndex"
            class="innerSlide"
          >
            <WaperItem
              :dragging="dragging"
              :waper="wapers[(wapIndex - 1) * nbLines + (wapInnerIndex - 1)]"
              :color="color"
            />
          </div>
        </Slide>
        <template #addons>
          <div
            class="custom-arrow slick-arrow slick-prev"
            :class="{ disabled: isFirstSlide }"
            @click="prev"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 23.64 14.973"
            >
              <path
                d="M23.64 7.487a.915.915 0 0 0-.222-.542l-6.3-6.7A.789.789 0 0 0 16 .215a.812.812 0 0 0-.031 1.114l5.06 5.371H.788a.788.788 0 0 0 0 1.576h20.241l-5.061 5.369A.825.825 0 0 0 16 14.758a.789.789 0 0 0 1.114-.031l6.3-6.7a.7.7 0 0 0 .222-.542Z"
              ></path>
            </svg>
          </div>
          <div
            class="custom-arrow slick-arrow slick-next"
            :class="{ disabled: isLastSlide }"
            @click="next"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 23.64 14.973"
            >
              <path
                d="M23.64 7.487a.915.915 0 0 0-.222-.542l-6.3-6.7A.789.789 0 0 0 16 .215a.812.812 0 0 0-.031 1.114l5.06 5.371H.788a.788.788 0 0 0 0 1.576h20.241l-5.061 5.369A.825.825 0 0 0 16 14.758a.789.789 0 0 0 1.114-.031l6.3-6.7a.7.7 0 0 0 .222-.542Z"
              ></path>
            </svg>
          </div>
        </template>
      </Carousel>
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { Carousel, Slide } from 'vue3-carousel'
import type {
  CarouselInstance,
  CarouselProps,
  SlideStartEvent,
  UserWithRoute,
} from '~/types'

const props = withDefaults(
  defineProps<{
    wapers?: UserWithRoute[]
    nbLines?: number
    color?: string
    animated?: boolean
    initialSlide?: number
  }>(),
  {
    wapers: () => [],
    nbLines: 3,
    color: 'white',
    animated: true,
    initialSlide: 0,
  },
)

const emit = defineEmits<{
  changeSlide: [slideNew: number]
}>()

const dragging = ref(false)

const carousel = ref<CarouselInstance>(null)
const carouselSize = useElementSize(carousel)

const { isMobile, isLargeMobile, isTabletPortrait } = useCustomMediaQuery()

const slideWidth = computed(() => {
  return isMobile.value || isLargeMobile.value || isTabletPortrait.value
    ? 302
    : 350
})

const carouselProps = computed(
  () =>
    ({
      transition: 500,
      touchDrag: true,
      snapAlign: 'start',
      itemsToShow: carouselSize.width.value / slideWidth.value,
    }) satisfies CarouselProps,
)

const carouselData = computed(() => carousel.value?.data)

const isFirstSlide = computed(() => {
  return carouselData.value && carouselData.value.currentSlide.value === 0
})

const isLastSlide = computed(
  () =>
    carouselData.value &&
    carouselData.value.currentSlide.value === carouselData.value.maxSlide.value,
)

const prev = () => {
  carousel.value?.prev()
}

const next = () => {
  carousel.value?.next()
}

const changeSlide = ({ slidingToIndex }: SlideStartEvent) => {
  dragging.value = true
  emit('changeSlide', slidingToIndex)
}

const afterSlide = () => {
  dragging.value = false
}

const maxLines = (waperIndex: number) => {
  return Math.min(
    props.nbLines,
    props.wapers.length - (waperIndex - 1) * props.nbLines,
  )
}
</script>

<style lang="scss">
.waper-slider-container {
  width: 100%;

  .carousel__track {
    display: flex;
    height: 100%;
  }

  .carousel__slide {
    display: flex;
    flex-direction: column;
    height: 100%;
    margin-right: 0;
  }

  .custom-arrow {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 8rem;
    height: 100%;
    padding: 0;
    font-size: 0;
    line-height: 0;
    background: $white;

    /* stylelint-disable-next-line declaration-property-value-disallowed-list */
    border: none;
    cursor: pointer;
    transform: translateX(100%);

    &.disabled {
      opacity: 0;
      pointer-events: none;
    }

    svg {
      width: 2.36rem;
    }

    &::before {
      content: '';
    }

    &.slick-prev {
      left: 0;
      transform: translateX(-100%);

      svg {
        transform: rotate(180deg);
      }
    }
  }

  .slide {
    .innerSlide {
      margin-bottom: 2rem;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

@media screen and (max-width: 700px) {
  .waper-slider-container {
    .carousel {
      padding-left: 2.5rem;
    }

    .custom-arrow {
      display: none;
    }
  }
}
</style>
